import { useState, useEffect, useCallback } from "react";
import { 
  Card, 
  DatePicker, 
  Spin, 
  message, 
  Table, 
  Tag, 
  Row, 
  Col, 
  Statistic,
  Typography,
  Space,
  Alert
} from "antd";
import { 
  UserOutlined,
} from "@ant-design/icons";
import withRoleAccess from "../../hoc/withRoleAccess";
import axios from "axios";
import dayjs from "dayjs";
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(isSameOrBefore);

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const CommissionDashboard = () => {
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([
    dayjs("2025-01-01"),
    dayjs("2025-12-31"),
  ]);
  const [commissionData, setCommissionData] = useState([]);
  const [commissionTiers, setCommissionTiers] = useState([]);

  const API_URL = process.env.REACT_APP_API_URL;

  // Fetch annual commission report by calling monthly reports for each month
  const fetchCommissionReport = useCallback(async (startDate, endDate) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const start = dayjs(startDate);
      const end = dayjs(endDate);
      
      const monthlyReports = [];
      let current = start.startOf('month');
      
      // Fetch data for each month in the range
      while (current.isSameOrBefore(end, 'month')) {
        const month = current.month() + 1;
        const year = current.year();
        
        try {
          const response = await axios.get(`${API_URL}/commission/monthly-report`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { month, year }
          });
          
          monthlyReports.push({
            month: month,
            year: year,
            monthName: current.format('MMM'),
            data: response.data || []
          });
        } catch (error) {
          console.warn(`No data for ${year}-${month}:`, error);
          monthlyReports.push({
            month: month,
            year: year,
            monthName: current.format('MMM'),
            data: []
          });
        }
        
        current = current.add(1, 'month');
      }
      
      // Process and organize data by salesperson
      const salespersonMap = {};
      
      monthlyReports.forEach((monthReport) => {
        monthReport.data.forEach(sp => {
          const spId = sp.salesperson.id || sp.salesperson.name;
          
          if (!salespersonMap[spId]) {
            salespersonMap[spId] = {
              salesperson: sp.salesperson,
              monthlyData: {},
              totalSales: 0,
              totalCommission: 0
            };
          }
          
          const salesAfterVAT = sp.totalSales * 0.93;
          const overTarget = sp.commission?.over_target || 0;
          
          salespersonMap[spId].monthlyData[monthReport.monthName] = {
            sales: sp.totalSales,
            salesAfterVAT: salesAfterVAT,
            overTarget: overTarget,
            commission: sp.commission?.total_commission || 0,
            breakdown: sp.commission?.breakdown || [],
            groups: sp.groups || [],
            totalGroups: sp.totalGroups || 0
          };
          
          salespersonMap[spId].totalSales += sp.totalSales;
          salespersonMap[spId].totalCommission += (sp.commission?.total_commission || 0);
        });
      });
      
      setCommissionData(Object.values(salespersonMap));
    } catch (error) {
      console.error("Error fetching commission report:", error);
      message.error("Failed to fetch commission report");
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  // Fetch commission tiers
  const fetchCommissionTiers = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/commission/tiers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCommissionTiers(response.data);
    } catch (error) {
      console.error("Error fetching commission tiers:", error);
      message.error("Failed to fetch commission tiers");
    }
  }, [API_URL]);

  useEffect(() => {
    fetchCommissionReport(
      dateRange[0].format("YYYY-MM-DD"),
      dateRange[1].format("YYYY-MM-DD")
    );
    fetchCommissionTiers();
  }, [dateRange, fetchCommissionReport, fetchCommissionTiers]);

  // Generate columns for the new structure
  const generateColumns = () => {
    return [
      {
        title: 'Month',
        dataIndex: 'month',
        key: 'month',
        width: 80,
        fixed: 'left',
        render: (month) => <Text strong>{month}</Text>,
      },
      {
        title: 'Target',
        dataIndex: 'target',
        key: 'target',
        width: 100,
        render: () => '฿600,000',
      },
      {
        title: 'Monthly Commission 2.5%',
        dataIndex: 'targetCommission',
        key: 'targetCommission',
        width: 120,
        render: (_, record) => {
          const salesAfterVAT = record.salesAfterVAT || 0;
          return salesAfterVAT >= 600000
            ? `฿${(600000 * 0.025).toLocaleString()}`
            : '฿0';
        },
      },
      {
        title: 'Sales',
        dataIndex: 'sales',
        key: 'sales',
        width: 120,
        render: (val) =>
          val ? `฿${parseFloat(val).toLocaleString()}` : '-',
      },
      {
        title: 'Deduct 7% VAT',
        dataIndex: 'salesAfterVAT',
        key: 'salesAfterVAT',
        width: 120,
        render: (val) => val ? `฿${parseFloat(val).toLocaleString()}` : '-',
      },
      {
        title: 'Over Target',
        dataIndex: 'overTarget',
        key: 'overTarget',
        width: 120,
        render: (val) => {
          if (val && val > 0) {
            return <Text strong style={{ color: '#ff4d4f' }}>฿{parseFloat(val).toLocaleString()}</Text>;
          }
          return '-';
        },
      },
      {
        title: 'Commission Rate',
        dataIndex: 'commissionRate',
        key: 'commissionRate',
        width: 100,
        render: (_, record) => {
          if (!record.breakdown || record.breakdown.length === 0) return '-';
          
          // Find the over target breakdown item
          const overTargetItem = record.breakdown.find(item => 
            item.tier_range !== "Base Target (600K)" && 
            item.tier_range !== "Below Target (600K)"
          );
          
          if (overTargetItem) {
            return <Text strong style={{ color: '#fa8c16', background: '#fff7e6', padding: '2px 6px', borderRadius: '4px' }}>
              {overTargetItem.rate}
            </Text>;
          }
          return '-';
        },
      },
      {
        title: 'Commission Amount',
        dataIndex: 'totalCommission',
        key: 'totalCommission',
        width: 120,
        render: (val) => val > 0 ? (
            <Text strong style={{ color: '#52c41a' }}>
            ฿{Number(val).toFixed(2).toLocaleString()}
            </Text>
        ) : '-'
        }

    ];
  };

  // Create individual salesperson table
  const createSalespersonTable = (salesperson, index) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    const tableData = months.map(month => {
      const monthData = salesperson.monthlyData[month] || {};
      
      return {
        key: month,
        month,
        target: 600000,
        sales: monthData.sales || 0,
        salesAfterVAT: monthData.salesAfterVAT || 0,
        overTarget: monthData.overTarget || 0,
        totalCommission: monthData.commission || 0,
        breakdown: monthData.breakdown || [],
        groups: monthData.groups || [],
        totalGroups: monthData.totalGroups || 0
      };
    });

    return (
      <Card 
        key={index}
        title={
          <Space>
            <UserOutlined />
            <span>
              <strong>{salesperson.salesperson.name}</strong>
              {salesperson.salesperson.nickname && (
                <Tag color="blue" style={{ marginLeft: 8 }}>
                  {salesperson.salesperson.nickname}
                </Tag>
              )}
            </span>
          </Space>
        }
        extra={
          <Space>
            <Text type="secondary">Total Courses: {salesperson.totalSales > 0 ? 
              Object.values(salesperson.monthlyData).reduce((sum, month) => sum + (month.totalGroups || 0), 0) : 0}
            </Text>
            <Text type="secondary">|</Text>
            <Text strong>Total Sales: ฿{salesperson.totalSales.toLocaleString()}</Text>
          </Space>
        }
        style={{ marginBottom: 24 }}
        size="small"
      >
        <Table
          columns={generateColumns()}
          dataSource={tableData}
          pagination={false}
          bordered
          size="small"
          scroll={{ x: 1200 }}
          expandable={{
            expandedRowRender: (record) => {
              if (record.groups.length === 0) {
                return <Text type="secondary">No groups for this month</Text>;
              }
              
              return (
                <div style={{ margin: '12px 0' }}>
                  <Text strong>Course Details for {record.month}:</Text>
                  <Table
                    size="small"
                    pagination={false}
                    bordered={false}
                    showHeader={true}
                    columns={[
                      {
                        title: 'Course Code',
                        dataIndex: 'class_code',
                        key: 'class_code',
                        width: 150,
                        render: (val) => <Text strong style={{ color: '#1890ff' }}>{val || 'No Code'}</Text>
                      },
                      {
                        title: 'Price',
                        dataIndex: 'price',
                        key: 'price',
                        width: 100,
                        render: (val) => <Text strong>฿{parseFloat(val).toLocaleString()}</Text>
                      },
                      {
                        title: 'Students',
                        dataIndex: 'students',
                        key: 'students',
                        width: 200,
                        render: (students) => {
                          if (!students || students.length === 0) {
                            return <Text type="secondary">No students</Text>;
                          }
                          return (
                            <div>
                              {students.slice(0, 2).map((student, idx) => (
                                <Tag key={idx} size="small" color="green">
                                  {student.nickname || `${student.firstname} ${student.lastname}`}
                                </Tag>
                              ))}
                              {students.length > 2 && (
                                <Tag size="small">+{students.length - 2} more</Tag>
                              )}
                            </div>
                          );
                        }
                      },
                      {
                        title: 'Create Date',
                        dataIndex: 'create_date',
                        key: 'create_date',
                        width: 100,
                        render: (val) => dayjs(val).format('DD/MM/YYYY')
                      }
                    ]}
                    dataSource={record.groups}
                    rowKey="groupID"
                    style={{ marginTop: 8 }}
                  />
                  <div style={{ marginTop: 8, textAlign: 'right' }}>
                    <Text strong>
                      Monthly Total: ฿{record.groups.reduce((sum, group) => sum + parseFloat(group.price), 0).toLocaleString()}
                    </Text>
                  </div>
                </div>
              );
            },
            rowExpandable: (record) => record.groups && record.groups.length > 0,
          }}
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}></Table.Summary.Cell>
                <Table.Summary.Cell index={1}></Table.Summary.Cell>
                <Table.Summary.Cell index={2}><Text strong style={{ fontSize: '18px' }}>Total</Text></Table.Summary.Cell>
                <Table.Summary.Cell index={3}>
                  
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4}>
                  <Text strong style={{ fontSize: '18px'}}>{salesperson.totalSales.toLocaleString()}</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5}>
                  <Text strong style={{ fontSize: '18px', color: '#52c41a' }}>{(salesperson.totalSales * 0.93).toLocaleString()}</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={6}>-</Table.Summary.Cell>
                <Table.Summary.Cell index={7}>-</Table.Summary.Cell>
                <Table.Summary.Cell index={8}>
                  <Text strong style={{ fontSize: '20px', color: '#52c41a' }}>
                    {Number(salesperson.totalCommission).toLocaleString(undefined, { 
                        minimumFractionDigits: 2, 
                        maximumFractionDigits: 2 
                    })}
                    </Text>

                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
      </Card>
    );
  };

  return (
    <div>
      {/* Header */}
      <Card style={{ marginBottom: 24 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={3} style={{ margin: 0 }}>
              Commission Management - Fixed Rate System
            </Title>
            <Text type="secondary">
              Single commission rate based on over-target tier placement
            </Text>
          </Col>
          <Col>
            <Space>
              <Text strong>Date Range:</Text>
              <RangePicker
                value={dateRange}
                onChange={(val) => setDateRange(val)}
                allowClear={false}
                format="YYYY-MM-DD"
                style={{ minWidth: 250 }}
              />
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Commission Rate Structure */}
      <Card title="Commission Rate Structure" style={{ marginBottom: 24 }}>
        <Row gutter={24}>
          <Col span={6}>
            <Statistic
              title="Monthly Target"
              value={600000}
              precision={0}
              prefix="฿"
              valueStyle={{ color: '#fa8c16' }}
              formatter={(value) => parseFloat(value).toLocaleString()}
            />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Base target for 2.5% commission
            </Text>
          </Col>
          <Col span={6}>
            <Statistic
              title="Base Commission"
              value={2.5}
              precision={1}
              suffix="%"
              valueStyle={{ color: '#1890ff' }}
            />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              On target amount (after VAT)
            </Text>
          </Col>
          <Col span={6}>
            <Statistic
              title="VAT Deduction"
              value={7}
              precision={0}
              suffix="%"
              valueStyle={{ color: '#ff4d4f' }}
            />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Applied before commission calculation
            </Text>
          </Col>
          <Col span={6}>
            <Statistic
              title="Active Salespersons"
              value={commissionData.length}
              valueStyle={{ color: '#722ed1' }}
            />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              With sales in selected period
            </Text>
          </Col>
        </Row>

        <div style={{ marginTop: 16, padding: 12, background: '#f6ffed', borderRadius: 6, border: '1px solid #b7eb8f' }}>
          <Text strong style={{ color: '#52c41a' }}>Commission Tiers (Applied to Over-Target Amount):</Text>
          <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            {commissionTiers.map((tier, idx) => (
              <Tag key={idx} color="green" style={{ fontSize: '12px' }}>
                ฿{parseFloat(tier.min_amount).toLocaleString()} - {tier.max_amount ? `฿${parseFloat(tier.max_amount).toLocaleString()}` : 'Above'}: {(tier.commission_rate * 100).toFixed(2)}%
              </Tag>
            ))}
          </div>
        </div>
      </Card>

      {/* Individual Salesperson Tables */}
      <Spin spinning={loading}>
        {commissionData.length > 0 ? (
          <div>
            {commissionData.map((salesperson, index) => 
              createSalespersonTable(salesperson, index)
            )}
          </div>
        ) : (
          <Card>
            <Text type="secondary">No commission data available for the selected date range.</Text>
          </Card>
        )}
      </Spin>
    </div>
  );
};

export default withRoleAccess(CommissionDashboard, ["superadmin"]);